import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { TransText } from '@/i18n/trans/text';
import { Button, Spinner, Typography } from '@packages/webshop-design-system';
import { type FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

/**
 * Landing page for the confirmation-email link. Reads the unverified profile
 * id + verification token from the URL, verifies the account, and forwards to
 * offer search. See BR-50961 / BR-56868.
 */
const SignupVerify: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyRegistration } = useAuth();
  const [hasError, setHasError] = useState(false);
  // Guard against double-invoke (React StrictMode / re-render) firing the
  // one-time verification call twice.
  const ran = useRef(false);

  usePageTitle('confirmRegistration');

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const id = searchParams.get('id');
    const token = searchParams.get('token');

    if (!id || !token) {
      setHasError(true);
      return;
    }

    verifyRegistration(id, token)
      .then(() => {
        toast.success(<TransText i18nKey="accountVerified" />);
        // Offer search — not /login. With TARA enabled, /login immediately
        // hands off to the IdP, which is wrong after email confirmation
        // (BR-56868).
        navigate('/purchase', { replace: true });
      })
      .catch(() => setHasError(true));
  }, [searchParams, verifyRegistration, navigate]);

  if (hasError) {
    return (
      <CenteredLayout>
        <div className="mx-auto flex w-full max-w-[410px] flex-col items-center gap-6 p-4">
          <Typography variant="body1" className="text-center">
            <TransText i18nKey="accountVerificationFailed" />
          </Typography>
          <Button
            data-testid="signup-verify-retry-button"
            size="medium"
            onClick={() => navigate('/signup', { replace: true })}
          >
            <TransText i18nKey="tryAgain" />
          </Button>
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full flex-col items-center gap-4 p-4">
        <Spinner size={40} />
        <Typography variant="body1">
          <TransText i18nKey="verifyingYourAccount" />
        </Typography>
      </div>
    </CenteredLayout>
  );
};

export default SignupVerify;
